<script setup lang="ts">
import type { TableColumn } from '@nuxt/ui'

import { isAxiosError } from 'axios'

import type { WorkspaceDTO, WorkspaceInviteDTO, WorkspaceInviteEventDTO } from '@/types'

import { toAppColor } from '@/composables/useAppColor'
import { appConfirm } from '@/composables/useAppConfirm'
import { useAppToast } from '@/composables/useAppToast'
import { deleteWorkspaceInvite, getWorkspaceInvites } from '@/services/workspaceService'

definePage({
  meta: {
    requiresAuth: true,
    requiresInternalAccess: true,
  },
})

const props = defineProps<{
  workspace: WorkspaceDTO
}>()

const { t, d, locale } = useI18n()
const toast = useAppToast()

type InviteStatusFilter = 'all' | 'active' | 'used' | 'expired'

const invites = ref<WorkspaceInviteDTO[]>([])
const loading = ref(false)
const deletingInviteIds = ref<Set<number>>(new Set())
const error = ref<string | null>(null)
const searchQuery = ref('')
const selectedStatus = ref<InviteStatusFilter>('all')

const statusOptions = computed(() => [
  { label: t('pages.workspaces.invites.filters.allStatuses'), value: 'all' as const },
  { label: t('components.invites.status.active'), value: 'active' as const },
  { label: t('components.invites.status.used'), value: 'used' as const },
  { label: t('components.invites.status.expired'), value: 'expired' as const },
])
const columns = computed<TableColumn<WorkspaceInviteDTO>[]>(() => [
  { accessorKey: 'invitedEmail', header: t('components.invites.email') },
  { id: 'scope', header: t('components.invites.scope') },
  { id: 'status', header: t('components.invites.status.label') },
  { id: 'usedFor', header: t('components.invites.usedFor') },
  { accessorKey: 'expiresAt', header: t('components.invites.expiresAt') },
  { id: 'createdBy', header: t('components.invites.createdBy') },
  { accessorKey: 'createdAt', header: t('components.invites.createdAt') },
  { id: 'actions', header: t('common.labels.actions') },
])

function formatEventLabel(
  nameDE: string,
  nameEN?: string | null,
  customId?: string | null,
): string {
  const localizedName = locale.value.startsWith('de') ? nameDE : (nameEN ?? nameDE)
  return customId ? `${localizedName} (${customId})` : localizedName
}

function formatEligibleEventLabel(event: WorkspaceInviteEventDTO): string {
  return formatEventLabel(event.nameDE, event.nameEN, event.customId)
}

function formatUsedEventLabel(invite: WorkspaceInviteDTO): string {
  if (!invite.usedEventNameDE) {
    return '—'
  }

  return formatEventLabel(invite.usedEventNameDE, invite.usedEventNameEN, invite.usedEventCustomId)
}

function getVisibleEligibleEvents(invite: WorkspaceInviteDTO): WorkspaceInviteEventDTO[] {
  return invite.eligibleEvents.length > 4
    ? invite.eligibleEvents.slice(0, 3)
    : invite.eligibleEvents.slice(0, 4)
}

function getHiddenEligibleEventCount(invite: WorkspaceInviteDTO): number {
  return invite.eligibleEvents.length > 4 ? invite.eligibleEvents.length - 3 : 0
}

function getStatus(invite: WorkspaceInviteDTO): Exclude<InviteStatusFilter, 'all'> {
  if (invite.usedAt) {
    return 'used'
  }

  if (invite.isValid) {
    return 'active'
  }

  return 'expired'
}

function getStatusLabel(invite: WorkspaceInviteDTO): string {
  switch (getStatus(invite)) {
    case 'used':
      return t('components.invites.status.used')
    case 'expired':
      return t('components.invites.status.expired')
    case 'active':
    default:
      return t('components.invites.status.active')
  }
}

function getStatusSeverity(
  invite: WorkspaceInviteDTO,
): 'success' | 'secondary' | 'danger' | 'warn' {
  switch (getStatus(invite)) {
    case 'used':
      return 'secondary'
    case 'expired':
      return 'danger'
    case 'active':
    default:
      return 'success'
  }
}

function matchesSearch(invite: WorkspaceInviteDTO, query: string): boolean {
  if (!query) {
    return true
  }

  const searchableParts = [
    invite.invitedEmail,
    invite.createdByName ?? '',
    invite.createdByUserId,
    formatUsedEventLabel(invite),
    ...invite.eligibleEvents.map(formatEligibleEventLabel),
  ]

  return searchableParts.some((value) => value.toLowerCase().includes(query))
}

const filteredInvites = computed(() => {
  const query = searchQuery.value.trim().toLowerCase()

  return invites.value.filter((invite) => {
    if (selectedStatus.value !== 'all' && getStatus(invite) !== selectedStatus.value) {
      return false
    }

    return matchesSearch(invite, query)
  })
})

const stats = computed(() => {
  const total = invites.value.length
  const active = invites.value.filter((invite) => getStatus(invite) === 'active').length
  const used = invites.value.filter((invite) => getStatus(invite) === 'used').length
  const expired = total - active - used

  return [
    {
      key: 'total',
      label: t('pages.workspaces.invites.stats.total'),
      value: total,
      icon: 'i-lucide-send',
    },
    {
      key: 'active',
      label: t('pages.workspaces.invites.stats.active'),
      value: active,
      icon: 'i-lucide-circle-check',
    },
    {
      key: 'used',
      label: t('pages.workspaces.invites.stats.used'),
      value: used,
      icon: 'i-lucide-square-check',
    },
    {
      key: 'expired',
      label: t('pages.workspaces.invites.stats.expired'),
      value: expired,
      icon: 'i-lucide-clock',
    },
  ]
})

async function loadInvites() {
  loading.value = true
  error.value = null

  try {
    invites.value = await getWorkspaceInvites(props.workspace.id)
  } catch (err) {
    console.error(`Error loading invites for workspace ${props.workspace.id}:`, err)
    error.value = t('pages.workspaces.invites.messages.loadError')
  } finally {
    loading.value = false
  }
}

async function confirmDeleteInvite(invite: WorkspaceInviteDTO) {
  const accepted = await appConfirm({
    message: t('components.invites.revokeConfirm', { email: invite.invitedEmail }),
    title: t('components.invites.revokeTitle'),
    cancelLabel: t('common.actions.cancel'),
    confirmLabel: t('components.invites.revokeAction'),
    confirmColor: 'error',
  })
  if (!accepted) return

  deletingInviteIds.value.add(invite.id)
  try {
    await deleteWorkspaceInvite(props.workspace.id, invite.id)
    toast.add({
      color: 'success',
      title: t('common.messages.success'),
      description: t('components.invites.revokeSuccess'),
      duration: 3000,
    })
    await loadInvites()
  } catch (err) {
    console.error(`Error deleting invite ${invite.id} from workspace ${props.workspace.id}:`, err)
    const detail =
      isAxiosError<{ errorKey?: string }>(err) && err.response?.data?.errorKey
        ? t(err.response.data.errorKey)
        : t('common.messages.errorGeneral')

    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: detail,
      duration: 4000,
    })
  } finally {
    deletingInviteIds.value.delete(invite.id)
  }
}

watch(
  () => props.workspace.id,
  () => {
    loadInvites()
  },
  { immediate: true },
)
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="flex flex-col gap-2">
      <h2 class="text-xl font-semibold">{{ t('pages.workspaces.invites.title') }}</h2>
      <p class="text-muted">
        {{ t('pages.workspaces.invites.subtitle') }}
      </p>
    </div>

    <AppAlert v-if="error" color="error" :closable="false">
      {{ error }}
    </AppAlert>

    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <div v-for="stat in stats" :key="stat.key" class="mycard flex items-center gap-4 p-4">
        <span
          class="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-primary-200 bg-primary-100 text-[var(--app-text-primary)] dark:border-primary-400/40 dark:bg-primary-400/20"
        >
          <UIcon class="text-lg leading-none" :name="stat.icon" />
        </span>
        <div class="flex min-w-0 flex-col">
          <span class="text-sm text-muted">{{ stat.label }}</span>
          <span class="text-2xl font-semibold">{{ stat.value }}</span>
        </div>
      </div>
    </div>

    <div class="mycard flex flex-col gap-4 p-4">
      <div class="flex flex-col gap-3 lg:flex-row lg:items-end">
        <div class="flex-1">
          <label for="workspace-invite-search" class="mb-1 block text-sm font-medium">
            {{ t('common.actions.search') }}
          </label>
          <AppIconField>
            <AppInputIcon name="i-lucide-search" />
            <UInput
              id="workspace-invite-search"
              v-model="searchQuery"
              :placeholder="t('pages.workspaces.invites.filters.searchPlaceholder')"
              class="w-full"
            />
          </AppIconField>
        </div>

        <div class="w-full lg:w-64">
          <label for="workspace-invite-status" class="mb-1 block text-sm font-medium">
            {{ t('pages.workspaces.invites.filters.status') }}
          </label>
          <AppSelect
            id="workspace-invite-status"
            v-model="selectedStatus"
            :options="statusOptions"
            option-label="label"
            option-value="value"
            class="w-full"
          />
        </div>
      </div>

      <AppSpinner v-if="loading" class="mx-auto" />

      <AppDataTableShell v-else :data="filteredInvites" :columns="columns" paginate :page-size="20">
        <template #empty>
          {{ t('pages.workspaces.invites.empty') }}
        </template>

        <template #scope-cell="{ row }">
          <div class="flex flex-col gap-1">
            <ul class="ml-3 list-disc text-sm">
              <li v-for="event in getVisibleEligibleEvents(row.original)" :key="event.id">
                {{ formatEligibleEventLabel(event) }}
              </li>
            </ul>
            <span v-if="getHiddenEligibleEventCount(row.original) > 0" class="text-sm text-muted">
              {{
                t('components.invites.moreEligibleEvents', {
                  count: getHiddenEligibleEventCount(row.original),
                })
              }}
            </span>
          </div>
        </template>

        <template #status-cell="{ row }">
          <UBadge
            :color="toAppColor(getStatusSeverity(row.original))"
            :label="getStatusLabel(row.original)"
          />
        </template>

        <template #usedFor-cell="{ row }">
          <span class="text-sm">{{ formatUsedEventLabel(row.original) }}</span>
        </template>

        <template #expiresAt-cell="{ row }">
          {{ d(new Date(row.original.expiresAt), 'datetime') }}
        </template>

        <template #createdBy-cell="{ row }">
          {{ row.original.createdByName || row.original.createdByUserId }}
        </template>

        <template #createdAt-cell="{ row }">
          <span v-if="row.original.createdAt">
            {{ d(new Date(row.original.createdAt), 'datetime') }}
          </span>
          <span v-else>—</span>
        </template>

        <template #actions-cell="{ row }">
          <UButton
            v-if="!row.original.usedAt"
            icon="i-lucide-trash"
            color="error"
            size="sm"
            :loading="deletingInviteIds.has(row.original.id)"
            :disabled="deletingInviteIds.has(row.original.id)"
            :aria-label="t('components.invites.revokeAction')"
            @click="confirmDeleteInvite(row.original)"
            variant="ghost"
          />
        </template>
      </AppDataTableShell>
    </div>
  </div>
</template>
